import React from 'react';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Tooltip from 'components/Tooltip/Tooltip';
import { capitalize } from 'utils/utils';
import { medicationsQuery } from 'pages/PatientRecords/PatientRecords';
import { usePatient } from 'context/appContext';
import { PAGE_TABS } from 'components/VisitRecords/VisitRecords';
import Skeleton from 'react-loading-skeleton';
import Graphic from 'components/Graphic/Graphic';
import { visitTypes } from 'context/visitContext';
import { Prescription } from '../../types/types';
import numberToWords from '../../utils/numberToWords';

function MedicationHistory() {
  const { patient } = usePatient();
  const [medicationsLimit, setMedicationsLimit] = React.useState(6);
  const navigate = useNavigate();
  const location = useLocation();

  const { data, isLoading, isFetching } = useQuery({
    ...medicationsQuery({ patientId: patient.id }),
    enabled: !!patient?.id,
  });

  const prescriptions = (data || []) as Prescription[];

  const getMedicationQty = (fndPresc: Prescription) => {
    if (fndPresc.numberDispensed && fndPresc.daysSupply && fndPresc.dispenseUnit) {
      return `${fndPresc.numberDispensed} ${fndPresc?.dispenseUnit?.toLocaleLowerCase()} / ${
        fndPresc.daysSupply || 0
      } ${(fndPresc.daysSupply || 0) > 1 ? 'days' : 'day'}`;
    }

    if (
      (!fndPresc.dose || fndPresc.dose === '-' || fndPresc.dose === '?') &&
      (!fndPresc.frequency || fndPresc.frequency === '-' || fndPresc.frequency === '?')
    ) {
      return '-';
    }

    const dose = `${fndPresc.dose}${
      fndPresc.numberToBeTaken
        ? `, ${numberToWords(parseInt(fndPresc.numberToBeTaken, 10))} (${fndPresc.numberToBeTaken})`
        : ''
    }`;

    const frequency = `, ${fndPresc.frequency}`;

    return `${dose.trim()}${frequency.trim()}`;
  };

  const getVisitType = (type: string) => {
    const findVisitType = visitTypes.find(fndType => fndType.type === type)?.name || 'N/A';
    return findVisitType === 'Sick' ? 'Sick Visit' : findVisitType;
  };

  const handleNextTab = () => {
    const tab = 'medication-history';

    const indexOfActiveTab = PAGE_TABS.findIndex(fndTab => fndTab.id === tab);

    const nextTab = PAGE_TABS.find((_, index) => index === indexOfActiveTab + 1);

    if (nextTab) {
      const tabURL = `${location.pathname.replace(tab, nextTab.id)}${location.search}`;
      navigate(tabURL);
    }
  };

  const renderLoading = () => (
    <div className="flex flex-col space-y-2">
      <div className="laptop:grid grid-cols-7 gap-6 pt-2 pb-4 text-[15px] font-medium hidden">
        <div className="col-span-2">
          <Skeleton width="100%" height={18} duration={1.4} />
        </div>
        <div>
          <Skeleton width="100%" height={18} duration={1.4} />
        </div>
        <div>
          <Skeleton width="100%" height={18} duration={1.4} />
        </div>
        <div>
          <Skeleton width="100%" height={18} duration={1.4} />
        </div>
        <div className="col-span-2">
          <Skeleton width="100%" height={18} duration={1.4} />
        </div>
      </div>

      {new Array(4).fill(null).map((_, index) => (
        <div
          className="laptop:grid laptop:grid-cols-7 gap-6 justify-between py-1 items-center"
          key={index}
        >
          <div className="col-span-2">
            <Skeleton width="100%" height={25} duration={1.4} />
          </div>
          <div>
            <Skeleton width="100%" height={25} duration={1.4} />
          </div>
          <div>
            <Skeleton width="100%" height={25} duration={1.4} />
          </div>
          <div className="hidden laptop:block">
            <Skeleton width="100%" height={25} duration={1.4} />
          </div>
          <div className="col-span-2 hidden laptop:block">
            <Skeleton width="100%" height={25} duration={1.4} />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col pt-0 pb-10 tablet:pt-5 tablet:pb-7">
      {(isLoading && isFetching) || !prescriptions ? (
        <>{renderLoading()}</>
      ) : prescriptions.length === 0 ? (
        <div className="flex flex-col justify-center items-center mt-8 mb-20">
          <Graphic id="graphicNoPendingVisit" className="h-[130px]" />
          <div className="text-center mt-3">
            <p className="font-medium">You’re all caught up.</p>
            <p className="text-neutral-500 text-xs">
              There are no listed medications for {`${patient.firstName} ${patient.lastName}`}.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="laptop:grid grid-cols-7 gap-6 pt-2 pb-3 text-[15px] font-semibold hidden">
            <div className="col-span-2">Medication name</div>
            <div>Refills</div>
            <div>Quantity</div>
            <div>Instructions</div>
            <div>Date</div>
            <div>Visit Type</div>
          </div>

          <div className="fs-exclude flex flex-col space-y-3">
            {prescriptions.slice(0, medicationsLimit).map(prescription => (
              <div
                className="hover:bg-[#F6FAFF] px-4 rounded-md -mx-4 py-1"
                key={`${prescription.id}${prescription.createdAt}`}
              >
                <div className="laptop:grid laptop:grid-cols-7 gap-6 flex justify-between py-1 items-center">
                  <div className="col-span-2 flex items-center laptop:space-x-8 space-x-3">
                    <div>
                      <div className="text-sm">{capitalize(prescription.medicationName)}</div>
                      <div className="flex items-center text-sm laptop:hidden">
                        {moment(prescription.createdAt).format('MM/DD/YYYY')}
                      </div>
                    </div>
                  </div>
                  <div className="laptop:flex items-center text-sm hidden">
                    {prescription.numberOfRefills}
                  </div>
                  <div className="laptop:flex items-center text-sm hidden">
                    {getMedicationQty(prescription)}
                  </div>
                  <div className="relative group laptop:flex hidden items-center text-sm cursor-pointer">
                    <p className="truncate">{prescription.instructions || '-'}</p>
                    {prescription.instructions && (
                      <Tooltip
                        content={prescription.instructions}
                        className="group-hover:after:!inline-block group-hover:before:!block
                      inline-flex after:!left-[50%] right-[40%]"
                      />
                    )}
                  </div>
                  <div className="laptop:flex hidden items-center text-sm">
                    {prescription.createdAt
                      ? moment(prescription.createdAt).format('MM/DD/YYYY')
                      : '-'}
                  </div>
                  <div>
                    <span
                      title={`${getVisitType(
                        prescription.visitType?.toLocaleUpperCase() || '',
                      )} Visit`}
                      className="bg-gray-200 text-[13px] rounded-full py-[3px] px-5 block
                      flex-shrink-0 whitespace-nowrap overflow-hidden text-ellipsis w-[130px] text-center"
                    >
                      {getVisitType(prescription.visitType?.toLocaleUpperCase() || '')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {prescriptions.length > 6 && medicationsLimit === 6 && (
        <div className="text-center pb-1 pt-3 text-river-blue">
          <button
            onClick={() => setMedicationsLimit(prescriptions.length)}
            type="button"
            className="p-2 !font-medium"
          >
            Show more medications...
          </button>
        </div>
      )}

      {!(isFetching && isLoading) && (
        <button
          type="button"
          onClick={() => handleNextTab()}
          className="w-fit border border-river-blue text-river-blue rounded-full px-5 py-[9px] text-sm 
          mt-10 tablet:mt-6 min-w-[180px] hover:bg-[#EEFBFF]"
        >
          Next
        </button>
      )}
    </div>
  );
}

export default MedicationHistory;
